/**
 * Promotions Analytics Component
 *
 * Comprehensive analytics for business owner promotions including:
 * - ROI metrics (tokens spent vs. impact)
 * - Promotion tier effectiveness comparison
 * - Category benchmarking
 * - Promotion spending history and trends
 */

import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartBar,
  faRocket,
  faTrophy,
  faCoins,
  faArrowUp,
  faSync,
  faSpinner,
  faFire,
  faStar,
  faCrown,
  faPlayCircle,
  faCalendarAlt,
  faClock,
} from '@fortawesome/free-solid-svg-icons';
import {
  collection,
  getDocs,
  onSnapshot,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { firestore } from '../FirebaseConfig';
import { useToast } from './ui/Toast';
import { Skeleton, ErrorMessage, EmptyState } from './ui';
import { formatShortDate } from '../utils/dateUtils';
import { groupStacks, tsToMs, formatCountdown } from '../utils/promotionStacks';
import '../styles/PromotionsAnalytics.css';

const PROMOTION_TIERS = {
  boost: { name: 'Quick Boost', tokenCost: 10, duration: '24h', icon: faRocket, color: '#0066cc' },
  spotlight: { name: 'Spotlight', tokenCost: 30, duration: '3d', icon: faFire, color: '#cc0000' },
  featured: { name: 'Featured', tokenCost: 50, duration: '7d', icon: faStar, color: '#b8860b' },
  premium: { name: 'Premium', tokenCost: 150, duration: '30d', icon: faCrown, color: '#8b0000' },
};

const PromotionsAnalytics = ({ userId, listingId, businessCategory }) => {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [promotionHistory, setPromotionHistory] = useState([]);
  const [metricsData, setMetricsData] = useState(null);
  const [benchmarkData, setBenchmarkData] = useState(null);
  const [selectedTier, setSelectedTier] = useState(null);
  const [dateRange, setDateRange] = useState('90days');

  const getDateRangeStart = useCallback(() => {
    const now = new Date();
    const map = {
      '30days': 30,
      '90days': 90,
      '180days': 180,
      'year': 365,
    };
    const days = map[dateRange] || 90;
    return new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
  }, [dateRange]);

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const startDate = Timestamp.fromDate(getDateRangeStart());

      // Load promotion history
      const promotionsQuery = query(
        collection(firestore, 'tokenTransactions'),
        where('userId', '==', userId),
        where('reason', '>=', 'listing_promotion:'),
        where('reason', '<', 'listing_promotion:~'),
        where('createdAt', '>=', startDate),
        orderBy('createdAt', 'desc')
      );

      const promotionsSnap = await getDocs(promotionsQuery);
      const promotions = [];

      promotionsSnap.forEach((docSnap) => {
        const tx = docSnap.data();
        const match = tx.reason?.match(/listing_promotion:(\w+)/);
        if (match) {
          promotions.push({
            id: docSnap.id,
            tier: match[1],
            tokenCost: tx.amount,
            appliedAt: tx.createdAt,
            listingId: tx.relatedId,
            ...tx,
          });
        }
      });

      setPromotionHistory(promotions);

      // Calculate metrics
      const metrics = calculateMetrics(promotions);
      setMetricsData(metrics);

      // Load benchmark data for category
      if (businessCategory) {
        const benchmarks = await loadCategoryBenchmarks(businessCategory, startDate);
        setBenchmarkData(benchmarks);
      }
    } catch (err) {
      console.error('Error loading promotions analytics:', err);
      setError('Failed to load promotion analytics');
    } finally {
      setLoading(false);
    }
  }, [userId, businessCategory, getDateRangeStart]);

  useEffect(() => {
    if (userId) {
      loadAnalytics();
    }
  }, [userId, loadAnalytics]);

  const calculateMetrics = (promotions) => {
    const tierStats = {};
    let totalTokensSpent = 0;
    let totalPromotions = 0;

    // Initialize tier stats
    Object.keys(PROMOTION_TIERS).forEach((tier) => {
      tierStats[tier] = {
        count: 0,
        totalSpent: 0,
        avgCost: 0,
        tier,
      };
    });

    // Aggregate by tier
    promotions.forEach((promo) => {
      const tier = promo.tier;
      if (tierStats[tier]) {
        tierStats[tier].count += 1;
        tierStats[tier].totalSpent += promo.tokenCost || 0;
        totalTokensSpent += promo.tokenCost || 0;
        totalPromotions += 1;
      }
    });

    // Calculate averages
    Object.keys(tierStats).forEach((tier) => {
      if (tierStats[tier].count > 0) {
        tierStats[tier].avgCost = Math.round(tierStats[tier].totalSpent / tierStats[tier].count);
      }
    });

    return {
      totalTokensSpent,
      totalPromotions,
      tierStats,
      avgCostPerPromotion: totalPromotions > 0 ? Math.round(totalTokensSpent / totalPromotions) : 0,
      mostUsedTier: Object.values(tierStats).reduce((a, b) => (a.count > b.count ? a : b), { tier: null }).tier,
    };
  };

  const loadCategoryBenchmarks = async (category, startDate) => {
    try {
      // Query all businesses in the same category
      const listingsQuery = query(
        collection(firestore, 'businessListings'),
        where('category', '==', category)
      );

      const listingsSnap = await getDocs(listingsQuery);
      const businessIds = [];

      listingsSnap.forEach((docSnap) => {
        businessIds.push(docSnap.id);
      });

      // If no other businesses, return null
      if (businessIds.length === 0) {
        return null;
      }

      // Query promotion spending for all businesses in category
      const spendingQuery = query(
        collection(firestore, 'tokenTransactions'),
        where('reason', '>=', 'listing_promotion:'),
        where('reason', '<', 'listing_promotion:~'),
        where('createdAt', '>=', startDate),
        orderBy('createdAt', 'desc')
      );

      const spendingSnap = await getDocs(spendingQuery);
      const categorySpending = {};

      spendingSnap.forEach((docSnap) => {
        const tx = docSnap.data();
        const userId = tx.userId;
        if (!categorySpending[userId]) {
          categorySpending[userId] = 0;
        }
        categorySpending[userId] += tx.amount || 0;
      });

      // Calculate benchmarks
      const spendingValues = Object.values(categorySpending);
      const avgCategorySpend = spendingValues.length > 0
        ? Math.round(spendingValues.reduce((a, b) => a + b, 0) / spendingValues.length)
        : 0;

      const maxCategorySpend = spendingValues.length > 0
        ? Math.max(...spendingValues)
        : 0;

      return {
        avgCategorySpend,
        maxCategorySpend,
        businessCount: businessIds.length,
        participatingCount: Object.keys(categorySpending).length,
      };
    } catch (err) {
      console.error('Error loading benchmarks:', err);
      return null;
    }
  };

  const handleRefresh = async () => {
    await loadAnalytics();
    showToast('Promotion analytics refreshed', 'success');
  };

  if (!userId) {
    return <EmptyState icon={faChartBar} title="No user data" message="Unable to load promotion analytics." />;
  }

  if (loading) {
    return (
      <div className="promotions-analytics">
        <header className="analytics-header">
          <h2>
            <FontAwesomeIcon icon={faChartBar} /> Promotions Analytics
          </h2>
          <Skeleton width={120} height={40} />
        </header>
        <div className="analytics-grid">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="analytics-card-skeleton">
              <Skeleton variant="circle" width={48} height={48} />
              <Skeleton width={100} height={24} />
              <Skeleton width={80} height={16} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="promotions-analytics">
        <header className="analytics-header">
          <h2>
            <FontAwesomeIcon icon={faChartBar} /> Promotions Analytics
          </h2>
        </header>
        <ErrorMessage message={error} onRetry={handleRefresh} />
      </div>
    );
  }

  return (
    <div className="promotions-analytics">
      <header className="analytics-header">
        <div className="analytics-title-section">
          <h2>
            <FontAwesomeIcon icon={faChartBar} /> Promotions Analytics
          </h2>
          <p>Track promotion spending, ROI, and performance across all tiers.</p>
        </div>
        <div className="analytics-controls">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="analytics-select"
          >
            <option value="30days">Last 30 Days</option>
            <option value="90days">Last 90 Days</option>
            <option value="180days">Last 180 Days</option>
            <option value="year">Last Year</option>
          </select>
          <button onClick={handleRefresh} className="analytics-refresh-btn" title="Refresh analytics">
            <FontAwesomeIcon icon={faSync} />
          </button>
        </div>
      </header>

      <ActiveScheduledPromotionsSummary listingId={listingId} />

      {/* Summary Metrics */}
      {metricsData && (
        <div className="analytics-summary">
          <div className="summary-card">
            <div className="card-icon">
              <FontAwesomeIcon icon={faCoins} />
            </div>
            <div className="card-content">
              <div className="card-label">Total Tokens Spent</div>
              <div className="card-value">{metricsData.totalTokensSpent.toLocaleString()}</div>
              <div className="card-meta">{metricsData.totalPromotions} promotions</div>
            </div>
          </div>

          <div className="summary-card">
            <div className="card-icon">
              <FontAwesomeIcon icon={faArrowUp} />
            </div>
            <div className="card-content">
              <div className="card-label">Average Per Promotion</div>
              <div className="card-value">{metricsData.avgCostPerPromotion}</div>
              <div className="card-meta">tokens</div>
            </div>
          </div>

          <div className="summary-card">
            <div className="card-icon">
              <FontAwesomeIcon icon={faTrophy} />
            </div>
            <div className="card-content">
              <div className="card-label">Most Used Tier</div>
              <div className="card-value">
                {metricsData.mostUsedTier ? PROMOTION_TIERS[metricsData.mostUsedTier]?.name : 'None'}
              </div>
              <div className="card-meta">
                {metricsData.mostUsedTier
                  ? `${metricsData.tierStats[metricsData.mostUsedTier].count} times`
                  : 'No promotions yet'}
              </div>
            </div>
          </div>

          {benchmarkData && (
            <div className="summary-card benchmark">
              <div className="card-icon">
                <FontAwesomeIcon icon={faChartBar} />
              </div>
              <div className="card-content">
                <div className="card-label">Category Comparison</div>
                <div className="card-value">
                  {metricsData.totalTokensSpent > benchmarkData.avgCategorySpend ? '↑' : '↓'} vs Avg
                </div>
                <div className="card-meta">
                  Your avg: {metricsData.avgCostPerPromotion} | Category: {benchmarkData.avgCategorySpend}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tier Breakdown */}
      {metricsData && (
        <section className="analytics-section">
          <h3 className="section-title">
            <FontAwesomeIcon icon={faRocket} /> Tier Breakdown
          </h3>
          <div className="tier-grid">
            {Object.entries(PROMOTION_TIERS).map(([tierKey, tier]) => {
              const tierData = metricsData.tierStats[tierKey];
              const isSelected = selectedTier === tierKey;

              return (
                <div
                  key={tierKey}
                  className={`tier-card ${isSelected ? 'active' : ''}`}
                  onClick={() => setSelectedTier(isSelected ? null : tierKey)}
                >
                  <div className="tier-icon" style={{ color: tier.color }}>
                    <FontAwesomeIcon icon={tier.icon} />
                  </div>
                  <div className="tier-name">{tier.name}</div>
                  <div className="tier-meta">
                    <span>{tier.tokenCost} tokens</span>
                    <span>{tier.duration}</span>
                  </div>
                  <div className="tier-stats">
                    <div className="stat-item">
                      <span className="stat-label">Used</span>
                      <span className="stat-value">{tierData.count}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Total</span>
                      <span className="stat-value">{tierData.totalSpent}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Promotion History Table */}
      {promotionHistory.length > 0 ? (
        <section className="analytics-section">
          <h3 className="section-title">
            <FontAwesomeIcon icon={faSpinner} /> Recent Promotions
          </h3>
          <div className="history-table-wrapper">
            <table className="history-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Tier</th>
                  <th>Tokens</th>
                  <th>Duration</th>
                  <th>Listing</th>
                </tr>
              </thead>
              <tbody>
                {promotionHistory.slice(0, 15).map((promo) => {
                  const tier = PROMOTION_TIERS[promo.tier];
                  return (
                    <tr key={promo.id}>
                      <td>{formatShortDate(promo.appliedAt)}</td>
                      <td>
                        <span className="tier-badge" style={{ color: tier?.color }}>
                          {tier?.name}
                        </span>
                      </td>
                      <td>{promo.tokenCost}</td>
                      <td>{tier?.duration}</td>
                      <td className="listing-id">{promo.listingId?.slice(0, 8)}...</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      ) : (
        <section className="analytics-section">
          <EmptyState
            icon={faRocket}
            title="No promotions yet"
            message="Start promoting your listing to see analytics here."
          />
        </section>
      )}

      {/* Category Benchmarking */}
      {benchmarkData && (
        <section className="analytics-section benchmark-section">
          <h3 className="section-title">
            <FontAwesomeIcon icon={faTrophy} /> Category Benchmarking
          </h3>
          <div className="benchmark-grid">
            <div className="benchmark-card">
              <div className="benchmark-label">Category Average Spend</div>
              <div className="benchmark-value">{benchmarkData.avgCategorySpend}</div>
              <div className="benchmark-meta">tokens per promotion</div>
            </div>
            <div className="benchmark-card">
              <div className="benchmark-label">Top Spender</div>
              <div className="benchmark-value">{benchmarkData.maxCategorySpend}</div>
              <div className="benchmark-meta">tokens</div>
            </div>
            <div className="benchmark-card">
              <div className="benchmark-label">Active Businesses</div>
              <div className="benchmark-value">{benchmarkData.participatingCount}</div>
              <div className="benchmark-meta">
                of {benchmarkData.businessCount} in {businessCategory}
              </div>
            </div>
            <div className="benchmark-card comparison">
              <div className="benchmark-label">Your Position</div>
              <div className="benchmark-value">
                {metricsData.totalTokensSpent > benchmarkData.avgCategorySpend
                  ? 'Above Average'
                  : metricsData.totalTokensSpent > 0
                  ? 'Below Average'
                  : 'Not Active'}
              </div>
              <div className="benchmark-meta">
                {metricsData.totalTokensSpent > benchmarkData.avgCategorySpend
                  ? 'High promotional investment'
                  : 'Opportunity to increase visibility'}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Tips & Recommendations */}
      <section className="analytics-section tips-section">
        <h3 className="section-title">
          <FontAwesomeIcon icon={faArrowUp} /> Optimization Tips
        </h3>
        <div className="tips-grid">
          <div className="tip-card">
            <div className="tip-icon">🎯</div>
            <div className="tip-content">
              <strong>Test Different Tiers</strong>
              <p>Try different promotion tiers to find the best ROI for your business.</p>
            </div>
          </div>
          <div className="tip-card">
            <div className="tip-icon">📊</div>
            <div className="tip-content">
              <strong>Monitor Performance</strong>
              <p>Track views, calls, and emails during and after each promotion to measure impact.</p>
            </div>
          </div>
          <div className="tip-card">
            <div className="tip-icon">💡</div>
            <div className="tip-content">
              <strong>Seasonal Strategy</strong>
              <p>Boost promotions during peak travel seasons to maximize visibility and bookings.</p>
            </div>
          </div>
          <div className="tip-card">
            <div className="tip-icon">⚡</div>
            <div className="tip-content">
              <strong>Quick Response</strong>
              <p>Monitor the Quick Boost tier for short-term events and time-sensitive offers.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

/**
 * ActiveScheduledPromotionsSummary
 * Subscribes to businessListings/{listingId}/promotionStacks and renders
 * compact Active / Scheduled rows so business owners see live promo state
 * directly from the analytics page.
 */
function ActiveScheduledPromotionsSummary({ listingId }) {
  const [stacks, setStacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setNowTick] = useState(0);

  useEffect(() => {
    if (!listingId) {
      setStacks([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const ref = collection(firestore, 'businessListings', listingId, 'promotionStacks');
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setStacks(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => unsub();
  }, [listingId]);

  useEffect(() => {
    const id = setInterval(() => setNowTick((n) => n + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  if (!listingId) return null;

  const { active, scheduled } = groupStacks(stacks);
  const total = active.length + scheduled.length;
  const manageHref = `/business-dashboard?tab=promotion&listingId=${listingId}`;

  return (
    <section className="promotions-analytics__active-summary">
      <header className="promotions-analytics__active-summary-header">
        <h3>
          <FontAwesomeIcon icon={faPlayCircle} /> Active &amp; Scheduled Promotions
        </h3>
        <a href={manageHref} className="promotions-analytics__active-manage">Manage promotions →</a>
      </header>
      {loading ? (
        <p className="promotions-analytics__active-empty">Loading promotions…</p>
      ) : total === 0 ? (
        <p className="promotions-analytics__active-empty">
          No promotions are running or scheduled for this listing right now.
        </p>
      ) : (
        <div className="promotions-analytics__active-grid">
          <SummaryColumn
            variant="active"
            title="Active now"
            icon={faPlayCircle}
            items={active}
            countdownKey="endAt"
            countdownLabel="ends in"
          />
          <SummaryColumn
            variant="scheduled"
            title="Scheduled"
            icon={faCalendarAlt}
            items={scheduled}
            countdownKey="startAt"
            countdownLabel="starts in"
          />
        </div>
      )}
    </section>
  );
}

function SummaryColumn({ variant, title, icon, items, countdownKey, countdownLabel }) {
  return (
    <div className={`promotions-analytics__active-col promotions-analytics__active-col--${variant}`}>
      <div className="promotions-analytics__active-col-title">
        <FontAwesomeIcon icon={icon} /> {title} <span>({items.length})</span>
      </div>
      {items.length === 0 ? (
        <p className="promotions-analytics__active-col-empty">None</p>
      ) : (
        <ul className="promotions-analytics__active-list">
          {items.map((s) => {
            const ms = tsToMs(s[countdownKey]);
            const delta = countdownKey === 'endAt' ? ms - Date.now() : ms - Date.now();
            const tierLabel = s.type === 'custom'
              ? 'Custom range'
              : (PROMOTION_TIERS[s.type]?.name || s.type);
            return (
              <li key={s.id} className="promotions-analytics__active-item">
                <span className="promotions-analytics__active-item-title">{tierLabel}</span>
                <span className="promotions-analytics__active-item-countdown">
                  <FontAwesomeIcon icon={faClock} /> {countdownLabel} {formatCountdown(delta)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default PromotionsAnalytics;
